// src/modules/competition/components/CompetitorsTable/TeamCompetitorsTableBody.js
import React from 'react';
import { ProgressBar, Tooltip, OverlayTrigger, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { DEFAULT_TEAM_ICON } from '../TeamManager';

const medals = ['🥇', '🥈', '🥉'];

const TeamCompetitorsTableBody = ({ competitors, teams, pointTarget, onUsernameClick }) => {
    // Build per-team grouped rows from the enriched teams array (sorted by totalPoints desc)
    // Fall back to raw teams if not yet enriched
    const competitorsByUsername = Object.fromEntries(
        competitors.map(c => [c.username, c])
    );

    // Use enriched teams list from the API (already sorted by totalPoints)
    // If teams not provided or empty, group competitors by their teamId attribute
    let sortedTeams = teams && teams.length > 0 ? teams : [];

    // Competitors without a team
    const unassigned = competitors.filter(c => !c.teamId);

    return (
        <tbody>
            {sortedTeams.map((team) => {
                const teamIndex = (team.rank ?? 1) - 1;
                const teamPoints = team.totalPoints ?? team.members?.reduce(
                    (sum, m) => sum + (competitorsByUsername[m]?.totalPoints ?? 0), 0
                ) ?? 0;
                const teamMeetsCriteria = teamPoints >= pointTarget;
                const teamProgress = pointTarget > 0 ? (teamPoints / pointTarget) * 100 : 0;
                const members = (team.members || [])
                    .map(m => competitorsByUsername[m])
                    .filter(Boolean)
                    .sort((a, b) => b.totalPoints - a.totalPoints);

                return (
                    <React.Fragment key={team.teamId}>
                        {/* Team header row */}
                        <tr className="table-active fw-bold">
                            <td style={{ whiteSpace: 'nowrap' }}>{teamIndex + 1} {medals[teamIndex] ?? ''}</td>
                            <td colSpan={1}>
                                <span className="me-1" style={{ fontSize: '1.1rem' }}>
                                    {team.icon || DEFAULT_TEAM_ICON}
                                </span>
                                {team.name}
                                <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                    {members.length} member{members.length !== 1 ? 's' : ''}
                                </Badge>
                            </td>
                            <td>{teamPoints.toFixed(2)}</td>
                            <td>{members.reduce((s, m) => s + (m.numActivities ?? 0), 0)}</td>
                            <td>
                                <div className="d-flex align-items-center">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            teamMeetsCriteria ? (
                                                <Tooltip id={`tt-team-${team.teamId}`}>Team achieved point target!</Tooltip>
                                            ) : (
                                                <Tooltip id={`tt-team-${team.teamId}`}>
                                                    {`${(pointTarget - teamPoints).toFixed(2)} points remaining`}
                                                </Tooltip>
                                            )
                                        }
                                    >
                                        <ProgressBar
                                            now={teamProgress}
                                            variant={teamMeetsCriteria ? 'success' : 'warning'}
                                            style={{ flex: 1, cursor: 'pointer' }}
                                        />
                                    </OverlayTrigger>
                                    <span className="ms-2">{teamMeetsCriteria ? '✅' : '❌'}</span>
                                </div>
                            </td>
                        </tr>

                        {/* Member rows */}
                        {members.map(competitor => {
                            const { username, firstName, lastName, totalPoints = 0, numActivities } = competitor;
                            return (
                                <tr
                                    key={username}
                                    onClick={() => onUsernameClick(username)}
                                    className="clickable-row"
                                    style={{ fontSize: '0.9em' }}
                                >
                                    <td></td>
                                    <td className="ps-4">
                                        {firstName} {lastName}
                                    </td>
                                    <td>{totalPoints.toFixed(2)}</td>
                                    <td>{numActivities}</td>
                                    <td></td>
                                </tr>
                            );
                        })}
                    </React.Fragment>
                );
            })}

            {/* Unassigned competitors (haven't joined a team yet) */}
            {unassigned.length > 0 && (
                <React.Fragment>
                    <tr className="table-secondary">
                        <td></td>
                        <td colSpan={4} className="fst-italic text-muted">No team yet</td>
                    </tr>
                    {unassigned.map(competitor => {
                        const { username, firstName, lastName, totalPoints = 0, numActivities } = competitor;
                        return (
                            <tr
                                key={username}
                                onClick={() => onUsernameClick(username)}
                                className="clickable-row"
                                style={{ fontSize: '0.9em' }}
                            >
                                <td></td>
                                <td className="ps-4">{firstName} {lastName}</td>
                                <td>{totalPoints.toFixed(2)}</td>
                                <td>{numActivities}</td>
                                <td></td>
                            </tr>
                        );
                    })}
                </React.Fragment>
            )}
        </tbody>
    );
};

TeamCompetitorsTableBody.propTypes = {
    competitors: PropTypes.arrayOf(PropTypes.shape({
        username: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        totalPoints: PropTypes.number.isRequired,
        numActivities: PropTypes.number.isRequired,
        teamId: PropTypes.string,
        teamName: PropTypes.string,
    })).isRequired,
    teams: PropTypes.arrayOf(PropTypes.shape({
        teamId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        members: PropTypes.arrayOf(PropTypes.string),
        totalPoints: PropTypes.number,
    })),
    pointTarget: PropTypes.number.isRequired,
    onUsernameClick: PropTypes.func.isRequired,
};

export default TeamCompetitorsTableBody;
